import { useState, useEffect } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Search, 
  CreditCard,
  Check,
  Clock,
  AlertCircle,
  Calendar,
  Plus,
  Download,
  History
} from "lucide-react";
import { API_BASE } from "../lib/api";

const API_URL = API_BASE;

const statusConfig = {
  en_attente: { label: "En attente", className: "bg-amber-900/50 text-amber-300 border-amber-800", icon: Clock },
  partiel: { label: "Partiel", className: "bg-blue-900/50 text-blue-300 border-blue-800", icon: History },
  paye: { label: "Payé", className: "bg-emerald-900/50 text-emerald-300 border-emerald-800", icon: Check },
  annule: { label: "Annulé", className: "bg-red-900/50 text-red-300 border-red-800", icon: AlertCircle },
};

const formatCurrency = (amount, devise = "FCFA") => {
  if (!amount) return "0 FCFA";
  if (devise === "EUR") {
    return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(amount);
  }
  return new Intl.NumberFormat("fr-FR").format(amount) + " FCFA";
};

export default function Paiements() {
  const [paiements, setPaiements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [versementDialogOpen, setVersementDialogOpen] = useState(false);
  const [selectedPaiement, setSelectedPaiement] = useState(null);
  const [versementForm, setVersementForm] = useState({
    montant: "",
    date_versement: new Date().toISOString().split("T")[0],
    mode_paiement: "virement",
    reference: "",
    notes: ""
  });

  useEffect(() => {
    fetchPaiements();
  }, []);

  const fetchPaiements = async () => {
    try {
      const response = await apiClient.get("/paiements");
      setPaiements(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des paiements");
    } finally {
      setLoading(false);
    }
  };

  const handleVersement = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post("/versements", {
        paiement_id: selectedPaiement.id,
        montant: parseFloat(versementForm.montant),
        date_versement: versementForm.date_versement,
        mode_paiement: versementForm.mode_paiement,
        reference: versementForm.reference,
        notes: versementForm.notes
      });
      toast.success("Versement enregistré");
      setVersementDialogOpen(false);
      setSelectedPaiement(null);
      setVersementForm({
        montant: "",
        date_versement: new Date().toISOString().split("T")[0],
        mode_paiement: "virement",
        reference: "",
        notes: ""
      });
      fetchPaiements();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const handleDownloadPDF = (paiementId) => {
    const token = localStorage.getItem("token");
    const url = `${API_URL}/paiements/${paiementId}/pdf`;
    
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(response => {
        if (!response.ok) throw new Error("Erreur");
        return response.blob();
      })
      .then(blob => {
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `facture_echeance_${paiementId.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.success("PDF téléchargé");
      })
      .catch(() => toast.error("Erreur de téléchargement"));
  };

  const openVersementDialog = (paiement) => {
    const montantRestant = paiement.reste_a_payer !== undefined ? paiement.reste_a_payer : paiement.montant;
    setSelectedPaiement(paiement);
    setVersementForm({
      montant: montantRestant.toString(),
      date_versement: new Date().toISOString().split("T")[0],
      mode_paiement: "virement",
      reference: "",
      notes: ""
    });
    setTimeout(() => setVersementDialogOpen(true), 0);
  };

  const filteredPaiements = paiements.filter((paiement) => {
    const searchMatch = 
      paiement.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      paiement.mission?.titre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      paiement.client?.nom?.toLowerCase().includes(searchTerm.toLowerCase());
    const statusMatch = filterStatus === "all" || paiement.statut === filterStatus;
    const typeMatch = filterType === "all" || paiement.type_paiement === filterType;
    return searchMatch && statusMatch && typeMatch;
  });

  const totals = {
    en_attente: filteredPaiements.filter(p => p.statut === "en_attente").reduce((sum, p) => sum + (p.reste_a_payer || p.montant || 0), 0),
    partiel: filteredPaiements.filter(p => p.statut === "partiel").reduce((sum, p) => sum + (p.reste_a_payer || 0), 0),
    paye: filteredPaiements.filter(p => p.statut === "paye").reduce((sum, p) => sum + (p.montant || 0), 0),
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="space-y-6" data-testid="paiements-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white tracking-tight">Paiements</h1>
          <p className="text-zinc-500 mt-1">Suivez vos encaissements et versements partiels</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-[#121214] border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-zinc-500">En attente</p>
                <p className="text-xl font-mono font-bold text-amber-400">{formatCurrency(totals.en_attente)}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#121214] border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-zinc-500">Partiellement payé</p>
                <p className="text-xl font-mono font-bold text-blue-400">{formatCurrency(totals.partiel)}</p>
              </div>
              <History className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#121214] border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-zinc-500">Encaissé</p>
                <p className="text-xl font-mono font-bold text-emerald-400">{formatCurrency(totals.paye)}</p>
              </div>
              <Check className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#121214] border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-zinc-500">Total créances</p>
                <p className="text-xl font-mono font-bold text-white">{formatCurrency(totals.en_attente + totals.partiel)}</p>
              </div>
              <CreditCard className="w-8 h-8 text-zinc-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-zinc-900/50 border-zinc-800"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[160px] bg-zinc-900/50 border-zinc-800">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent className="bg-[#121214] border-zinc-800">
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="en_attente">En attente</SelectItem>
            <SelectItem value="partiel">Partiel</SelectItem>
            <SelectItem value="paye">Payé</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[160px] bg-zinc-900/50 border-zinc-800">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent className="bg-[#121214] border-zinc-800">
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="acompte">Acompte</SelectItem>
            <SelectItem value="echeance">Échéance</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Versement Dialog */}
      <Dialog open={versementDialogOpen} onOpenChange={setVersementDialogOpen}>
        <DialogContent className="bg-[#121214] border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">Enregistrer un versement</DialogTitle>
            <DialogDescription className="text-zinc-500">
              {selectedPaiement && (
                <>
                  Échéance: {selectedPaiement.notes} <br/>
                  Reste à payer: <span className="text-amber-400 font-mono">{formatCurrency(selectedPaiement.reste_a_payer || selectedPaiement.montant)}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleVersement} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">Montant versé *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max={selectedPaiement?.reste_a_payer || selectedPaiement?.montant}
                value={versementForm.montant}
                onChange={(e) => setVersementForm({...versementForm, montant: e.target.value})}
                required
                className="bg-zinc-900/50 border-zinc-800 font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Date du versement *</Label>
              <Input
                type="date"
                value={versementForm.date_versement}
                onChange={(e) => setVersementForm({...versementForm, date_versement: e.target.value})}
                required
                className="bg-zinc-900/50 border-zinc-800"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Mode de paiement</Label>
              <Select
                value={versementForm.mode_paiement}
                onValueChange={(v) => setVersementForm({...versementForm, mode_paiement: v})}
              >
                <SelectTrigger className="bg-zinc-900/50 border-zinc-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121214] border-zinc-800">
                  <SelectItem value="virement">Virement bancaire</SelectItem>
                  <SelectItem value="cheque">Chèque</SelectItem>
                  <SelectItem value="especes">Espèces</SelectItem>
                  <SelectItem value="mobile_money">Mobile Money</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Référence</Label>
              <Input
                value={versementForm.reference}
                onChange={(e) => setVersementForm({...versementForm, reference: e.target.value})}
                className="bg-zinc-900/50 border-zinc-800 font-mono"
                placeholder="N° chèque, référence virement..."
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Notes</Label>
              <Textarea
                value={versementForm.notes}
                onChange={(e) => setVersementForm({...versementForm, notes: e.target.value})}
                className="bg-zinc-900/50 border-zinc-800"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setVersementDialogOpen(false)} className="border-zinc-700">
                Annuler
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700">
                <Check className="w-4 h-4 mr-2" />
                Enregistrer
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Paiements List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12 text-zinc-500">Chargement...</div>
        ) : filteredPaiements.length === 0 ? (
          <Card className="bg-[#121214] border-zinc-800">
            <CardContent className="py-12 text-center">
              <CreditCard className="w-12 h-12 mx-auto text-zinc-700 mb-4" />
              <p className="text-zinc-500">Aucun paiement trouvé</p>
            </CardContent>
          </Card>
        ) : (
          filteredPaiements.map((paiement) => {
            const isOverdue = paiement.statut !== "paye" && paiement.date_prevue < today;
            const StatusIcon = statusConfig[paiement.statut]?.icon || Clock;
            const montantVerse = paiement.montant_verse || 0;
            const resteAPayer = paiement.reste_a_payer || paiement.montant;
            const progressPercent = paiement.montant > 0 ? (montantVerse / paiement.montant) * 100 : 0;
            
            return (
              <Card 
                key={paiement.id} 
                className={`bg-[#121214] border-zinc-800 ${isOverdue ? 'border-l-4 border-l-red-500' : ''}`}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        paiement.statut === 'paye' ? 'bg-emerald-900/30' :
                        paiement.statut === 'partiel' ? 'bg-blue-900/30' :
                        isOverdue ? 'bg-red-900/30' : 'bg-amber-900/30'
                      }`}>
                        <StatusIcon className={`w-5 h-5 ${
                          paiement.statut === 'paye' ? 'text-emerald-400' :
                          paiement.statut === 'partiel' ? 'text-blue-400' :
                          isOverdue ? 'text-red-400' : 'text-amber-400'
                        }`} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <p className="font-medium text-white">{paiement.mission?.titre || "Mission"}</p>
                          <Badge variant="outline" className={`text-xs ${
                            paiement.type_paiement === 'acompte' ? 'border-blue-700 text-blue-400' : 'border-zinc-700 text-zinc-400'
                          }`}>
                            {paiement.type_paiement === 'acompte' ? 'Acompte' : `Échéance ${paiement.numero_echeance}`}
                          </Badge>
                          <Badge variant="outline" className={statusConfig[paiement.statut]?.className}>
                            {statusConfig[paiement.statut]?.label}
                          </Badge>
                          {isOverdue && (
                            <Badge className="bg-red-900/50 text-red-300">En retard</Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-zinc-500 mb-2">
                          {paiement.client && <span>{paiement.client.nom}</span>}
                          <span className="flex items-center gap-1 font-mono">
                            <Calendar className="w-3 h-3" />
                            {paiement.date_prevue}
                          </span>
                        </div>

                        {/* Progress bar pour versements partiels */}
                        {(paiement.statut === "partiel" || montantVerse > 0) && (
                          <div className="mt-2">
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-zinc-500">Versé: {formatCurrency(montantVerse)}</span>
                              <span className="text-zinc-500">{progressPercent.toFixed(0)}%</span>
                            </div>
                            <Progress value={progressPercent} className="h-2" />
                          </div>
                        )}

                        {/* Historique versements */}
                        {paiement.versements && paiement.versements.length > 0 && (
                          <div className="mt-2 text-xs text-zinc-500">
                            <p className="mb-1">{paiement.versements.length} versement(s):</p>
                            <div className="flex flex-wrap gap-2">
                              {paiement.versements.slice(-3).map((v, i) => (
                                <Badge key={i} variant="outline" className="border-zinc-700 text-zinc-400 font-mono">
                                  {v.date_versement}: {formatCurrency(v.montant)} ({v.mode_paiement})
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm text-zinc-500">Total</p>
                        <p className="text-lg font-mono font-bold text-white">
                          {formatCurrency(paiement.montant, paiement.devise)}
                        </p>
                        {resteAPayer > 0 && resteAPayer < paiement.montant && (
                          <p className="text-sm font-mono text-amber-400">
                            Reste: {formatCurrency(resteAPayer)}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        {paiement.statut !== "paye" && (
                          <Button
                            size="sm"
                            onClick={() => openVersementDialog(paiement)}
                            className="bg-emerald-600 hover:bg-emerald-700"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Versement
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadPDF(paiement.id)}
                          className="border-zinc-700 text-zinc-300"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          PDF
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}
